import React, { useState, useEffect } from 'react';

const PROFILES = [
    {
        id: 'Balanced',
        label: 'Balanced',
        icon: '⚖️',
        description: 'Standard 40/30/30 distribution. Ideal for maintenance.',
        color: 'var(--profile-balanced)',
        ratios: { carbs: 40, protein: 30, fat: 30 }
    },
    {
        id: 'High Protein',
        label: 'High Protein',
        icon: '💪',
        description: 'Muscle-focused | 45% Protein. Prioritizes satiety.',
        color: 'var(--profile-high-protein)',
        ratios: { carbs: 25, protein: 45, fat: 30 }
    },
    {
        id: 'Keto',
        label: 'Keto',
        icon: '🥑',
        description: 'Ultra low-carb | 70% Fat. Ketogenic distribution.',
        color: 'var(--profile-keto)',
        ratios: { carbs: 5, protein: 25, fat: 70 }
    }
];

export const MacroProfileSelector = ({ value, onChange, isLoading }) => {
    const [localValue, setLocalValue] = useState(value);
    const [isTransitioning, setIsTransitioning] = useState(false);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    const handleSelect = (profileId) => {
        if (isLoading) return;

        // Toggle handling
        const newValue = profileId === localValue ? null : profileId;

        setIsTransitioning(true);
        setLocalValue(newValue);
        onChange(newValue);

        setTimeout(() => {
            setIsTransitioning(false);
        }, 300);
    };

    const activeProfile = PROFILES.find(p => p.id === localValue);

    return (
        <div className="animate-enter" style={{ animationDelay: '0.2s' }}>
            <div className="flex items-center gap-4 mb-6">
                <div className="h-px bg-[var(--glass-border)] flex-1" />
                <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-[0.3em] whitespace-nowrap">Macro Goals</span>
                    {!localValue && (
                        <span className="custom-mode-badge animate-enter">Custom</span>
                    )}
                </div>
                <div className="h-px bg-[var(--glass-border)] flex-1" />
            </div>

            <div
                role="radiogroup"
                aria-label="Macronutrient Profile"
                className="variety-segments"
            >
                {PROFILES.map((profile) => (
                    <button
                        key={profile.id}
                        role="radio"
                        aria-checked={localValue === profile.id}
                        aria-label={`${profile.label} - ${profile.description}`}
                        disabled={isLoading}
                        className={`variety-segment active:scale-95 transition-all ${localValue === profile.id ? 'active' : ''}`}
                        style={{
                            '--active-color-rgb': profile.id === 'Balanced' ? '59, 130, 246' : profile.id === 'High Protein' ? '16, 185, 129' : '245, 158, 11'
                        }}
                        onClick={() => handleSelect(profile.id)}
                    >
                        <span className="text-xl mb-1" role="img" aria-hidden="true">{profile.icon}</span>
                        <span className="text-[10px] font-black uppercase tracking-tight mb-2">{profile.label}</span>

                        <div className="ratio-bar">
                            <div className="ratio-bar-segment bg-blue-400" style={{ width: `${profile.ratios.carbs}%` }} />
                            <div className="ratio-bar-segment bg-emerald-400" style={{ width: `${profile.ratios.protein}%` }} />
                            <div className="ratio-bar-segment bg-amber-400" style={{ width: `${profile.ratios.fat}%` }} />
                        </div>

                        <div className="flex justify-between w-full mt-1 px-1">
                            <span className="text-[8px] font-bold text-blue-500">{profile.ratios.carbs}%</span>
                            <span className="text-[8px] font-bold text-emerald-500">{profile.ratios.protein}%</span>
                            <span className="text-[8px] font-bold text-amber-500">{profile.ratios.fat}%</span>
                        </div>
                    </button>
                ))}
            </div>

            <div className={`text-center mt-4 h-12 flex flex-col justify-center transition-all duration-300 ${isTransitioning ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>
                <p className="text-sm font-bold text-[var(--text-primary)]" style={{ color: activeProfile?.color }}>
                    {activeProfile ? activeProfile.description : 'Custom Configuration: Uses manual target inputs below.'}
                </p>
                {activeProfile && (
                    <p className="text-[10px] text-[var(--text-secondary)] font-black uppercase tracking-widest mt-1">
                        Chef will prioritize {activeProfile.id} distribution
                    </p>
                )}
            </div>

            {isLoading && (
                <div className="h-0.5 w-full bg-transparent overflow-hidden mt-2">
                    <div className="h-full bg-[var(--accent-color)] animate-shimmer" style={{ width: '100%' }} />
                </div>
            )}
        </div>
    );
};
